import { useQuery } from '@tanstack/react-query';
import foodsService, {
  GetFoodsParams,
} from '../services/foodsService/foods.service';
import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { Period } from '~/constants/period.constants';
import { useLocale } from './useLocale';

export const FOODS_QUERY_KEY = 'foods' as const;

export type UseFoodsOptions = Partial<GetFoodsParams> & {
  enabled?: boolean;
};

export const useFoods = (options: UseFoodsOptions = {}) => {
  const router = useRouter();
  const { id } = router.query;
  const { currentLocale } = useLocale();

  // Single food query
  const foodQuery = useQuery({
    queryKey: [FOODS_QUERY_KEY, 'detail', id],
    queryFn: () => foodsService.getFoodById(Number(id), currentLocale.language),
    enabled: !!id && options.enabled !== false,
  });

  // Foods list query
  const foodsQuery = useQuery({
    queryKey: [FOODS_QUERY_KEY, 'list', options],
    queryFn: () =>
      foodsService.getFoods({
        page: Number(router.query.page) || 1,
        limit: 20,
        sort: options.sort,
        period: options.period,
        ...options,
      }),
    enabled: !id && options.enabled !== false,
  });

  const handlePageChange = useCallback(
    (newPage: number) => {
      router.push({
        pathname: router.pathname,
        query: { ...router.query, page: newPage },
      });
    },
    [router],
  );

  const handleSearch = useCallback(
    (searchTerm: string) => {
      router.push({
        pathname: router.pathname,
        query: { ...router.query, search: searchTerm, page: 1 },
      });
    },
    [router],
  );

  const handlePeriodChange = useCallback(
    (period: Period) => {
      router.push({
        pathname: router.pathname,
        query: { ...router.query, period, page: 1 },
      });
    },
    [router],
  );

  const handleSortChange = useCallback(
    (sort: string) => {
      router.push({
        pathname: router.pathname,
        query: { ...router.query, sort, page: 1 },
      });
    },
    [router],
  );

  const handleCategoryChange = useCallback(
    (categoryId: number | undefined) => {
      router.push({
        pathname: router.pathname,
        query: { ...router.query, categoryId, page: 1 },
      });
    },
    [router],
  );

  return {
    // Single food data and states
    food: foodQuery.data,
    isFoodLoading: foodQuery.isLoading,
    foodError: foodQuery.error,
    refetchFood: foodQuery.refetch,

    // Foods list data and states
    foods: foodsQuery.data?.items ?? [],
    total: foodsQuery.data?.total ?? 0,
    currentPage: foodsQuery.data?.page ?? 1,
    totalPages: foodsQuery.data?.totalPages ?? 1,
    hasMore: foodsQuery.data?.hasMore ?? false,
    isLoading: foodsQuery.isLoading,
    error: foodsQuery.error,
    refetch: foodsQuery.refetch,

    // Handlers
    handlePageChange,
    handleSearch,
    handlePeriodChange,
    handleSortChange,
    handleCategoryChange,
  };
};
